import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { useSidebar } from '@/shared/ui/layout/SidebarContext';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { Button } from '@/shared/ui/buttons/Button';
import { cn } from '@/shared/utils/cn';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage, extractFieldErrors } from '@/shared/api/errors';
import { apiParcelsPost, apiParcelsPut, useParcelDetail } from '@/entities/parcels';
import { useCargoIdGateStatus } from '@/entities/cargo-id';
import { FeaturesCargoIdParcelCreateGate } from '@/features/cargo-id';
import {
  FeaturesParcelFormRecipientPicker,
  FeaturesParcelFormSectionBasic,
  FeaturesParcelFormSectionPersonal,
  emptyParcelFormValues,
  parcelFormSchema,
  type ParcelFormValues,
} from '@/features/parcel-form';
import type { ApiParcelsGetItem, ApiParcelsPostBody } from '@/shared/types/api/parcels';

const mapItemToFormValues = (item: ApiParcelsGetItem): ParcelFormValues => ({
  name: item.name,
  trek_id: item.trek_id,
  store_name: item.store_name ?? '',
  address_name: item.address_name,
  region: item.region,
  city: item.city,
  street: item.street,
  building: item.building,
  apartment: item.apartment ?? '',
  additional_info: item.additional_info ?? '',
  first_name: item.first_name,
  last_name: item.last_name,
  middle_name: item.middle_name,
  phone_number: item.phone_number,
  passport_series: item.passport_series,
  pinfl: item.pinfl,
});

const toPayload = (values: ParcelFormValues): ApiParcelsPostBody => ({
  name: values.name,
  trek_id: values.trek_id,
  store_name: values.store_name || undefined,
  address_name: values.address_name,
  region: values.region,
  city: values.city,
  street: values.street,
  building: values.building,
  apartment: values.apartment || undefined,
  additional_info: values.additional_info || undefined,
  first_name: values.first_name,
  last_name: values.last_name,
  middle_name: values.middle_name,
  phone_number: values.phone_number,
  passport_series: values.passport_series,
  pinfl: values.pinfl,
});

export const WidgetsParcelCreateMain = () => {
  const { t } = useTranslation('parcelForm');
  const navigate = useNavigate();
  const toast = useToast();
  const { collapsed } = useSidebar();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit') || '';
  const isEdit = Boolean(editId);

  const detail = useParcelDetail(isEdit ? editId : undefined);
  const gate = useCargoIdGateStatus();

  const form = useForm<ParcelFormValues>({
    resolver: zodResolver(parcelFormSchema),
    defaultValues: emptyParcelFormValues,
    mode: 'onBlur',
  });

  const parcel = detail.data;
  useEffect(() => {
    if (parcel) {
      form.reset(mapItemToFormValues(parcel));
    }
    // reset once per loaded parcel; refetches return new object refs and must not clobber edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcel?.id]);

  const mutation = useMutate({
    api: (payload: ApiParcelsPostBody) =>
      isEdit ? apiParcelsPut(editId, payload) : apiParcelsPost(payload),
    invalidateKeys: isEdit
      ? [queryKeys.parcels.all, queryKeys.parcels.detail(editId), queryKeys.pinfl.all]
      : [queryKeys.parcels.all, queryKeys.pinfl.all],
    onSuccess: (saved) => {
      if (isEdit) {
        toast.success(t('toast.updated'));
        navigate(`/parcels/${editId}`);
        return;
      }
      toast.success(t('toast.savedDraft'));
      navigate(`/parcels/${saved.id}/declaration`);
    },
    onError: (err, message) => {
      // Race: backend requires a Cargo ID at create time (e.g. it was unbound in
      // another tab). If we hit 403 during the create POST, surface the API message
      // verbatim and route the user to /ids so they can get/connect one.
      if (!isEdit && err instanceof AxiosError && err.response?.status === 403) {
        toast.error(message);
        navigate('/ids');
        return;
      }
      const fieldErrors = extractFieldErrors(err);
      Object.entries(fieldErrors).forEach(([field, msg]) => {
        if (field in emptyParcelFormValues) {
          form.setError(field as keyof ParcelFormValues, { type: 'server', message: msg });
        }
      });
      toast.error(message);
    },
  });

  const onSubmit = (values: ParcelFormValues): void => {
    mutation.mutate(toPayload(values));
  };

  const title = isEdit ? t('titleEdit') : t('titleCreate');

  if (isEdit && detail.isLoading) {
    return (
      <>
        <PageHeader title={title} subtitle={t('subtitle')} showBack />
        <div className="flex min-h-screen items-center justify-center">
          <Spinner size="lg" />
        </div>
      </>
    );
  }

  // Cargo ID gate (create only). Editing existing parcels is not gated since the
  // backend rule applies to creation. A user who lands here directly without an
  // Cargo ID must not see an empty form.
  if (!isEdit && gate.status !== 'ready') {
    return (
      <>
        <PageHeader title={title} subtitle={t('subtitle')} showBack />
        <FeaturesCargoIdParcelCreateGate />
      </>
    );
  }

  return (
    <>
      <PageHeader title={title} subtitle={t('subtitle')} showBack />
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 p-4 md:pb-40 lg:gap-6 lg:p-6 lg:pb-44"
        data-cy="parcel-create-form"
      >
        <FeaturesParcelFormSectionBasic form={form} />
        <FeaturesParcelFormRecipientPicker form={form} />
        <FeaturesParcelFormSectionPersonal form={form} />

        <div
          className={cn(
            'mt-2 border-t border-slate-200 bg-white/95 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] backdrop-blur transition-[margin] md:fixed md:inset-x-0 md:bottom-0 md:z-20 md:mt-0',
            collapsed ? 'md:ml-16' : 'md:ml-60 lg:ml-64',
          )}
        >
          <div className="mx-auto flex max-w-3xl gap-2 lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl lg:px-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={mutation.isPending}
              block
            >
              {t('cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending} block>
              {isEdit ? t('save') : t('saveAndContinue')}
            </Button>
          </div>
          {mutation.error && (
            <p role="alert" className="mt-2 text-xs text-red-600">
              {extractErrorMessage(mutation.error)}
            </p>
          )}
        </div>
      </form>
    </>
  );
};
